import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Layout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Services', href: '/services' },
    { name: 'ASD Assessment', href: '/asd-assessment' },
    { name: 'ADHD Assessment', href: '/adhd-assessment' },
    { name: 'Therapy', href: '/therapy' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <nav className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3" data-testid="logo-link">
              <img 
                src="/images/logo.png" 
                alt="Navigating Minds Psychology"
                className="h-32 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-link-${item.name.toLowerCase().replace(' ', '-')}`}
                  className={`text-sm font-medium transition-colors hover:text-[var(--blue-primary)] ${
                    isActive(item.href) ? 'text-[var(--blue-primary)] border-b-2 border-[var(--blue-primary)]' : 'text-[var(--text-secondary)]'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <Link
                to="/contact"
                data-testid="header-cta-button"
                className="px-6 py-2 bg-[var(--blue-primary)] text-white rounded-full font-medium hover:bg-[var(--button-primary-hover)] transition-all duration-300 hover:scale-105 hover:shadow-lg"
              >
                Book Now
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-[var(--warm-gray)] transition-colors"
              data-testid="mobile-menu-button"
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="lg:hidden mt-4 pb-4"
              >
                <div className="flex flex-col space-y-4">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid={`mobile-nav-link-${item.name.toLowerCase().replace(' ', '-')}`}
                      className={`text-base font-medium transition-colors hover:text-[var(--blue-primary)] py-2 ${
                        isActive(item.href) ? 'text-[var(--blue-primary)]' : 'text-[var(--text-secondary)]'
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                  <Link
                    to="/contact"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-cta-button"
                    className="px-6 py-3 bg-[var(--blue-primary)] text-white rounded-full font-medium hover:bg-[var(--button-primary-hover)] transition-colors text-center"
                  >
                    Book Now
                  </Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </nav>
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-[var(--text-primary)] text-white">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* About Column */}
            <div>
              <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)', color: 'white' }}>
                Navigating Minds
              </h3>
              <p className="text-sm text-gray-300 mb-4">
                Professional telehealth psychology services specialising in therapy and ASD/ADHD assessments.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-4" style={{ fontFamily: 'var(--font-heading)', color: 'white' }}>Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-sm text-gray-300 hover:text-white transition-colors">Home</Link></li>
                <li><Link to="/about" className="text-sm text-gray-300 hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/services" className="text-sm text-gray-300 hover:text-white transition-colors">Services</Link></li>
                <li><Link to="/contact" className="text-sm text-gray-300 hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-lg font-semibold mb-4" style={{ fontFamily: 'var(--font-heading)', color: 'white' }}>Services</h4>
              <ul className="space-y-2">
                <li><Link to="/asd-assessment" className="text-sm text-gray-300 hover:text-white transition-colors">ASD Assessment</Link></li>
                <li><Link to="/adhd-assessment" className="text-sm text-gray-300 hover:text-white transition-colors">ADHD Assessment</Link></li>
                <li><Link to="/therapy" className="text-sm text-gray-300 hover:text-white transition-colors">Therapy Services</Link></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-lg font-semibold mb-4" style={{ fontFamily: 'var(--font-heading)', color: 'white' }}>Contact Us</h4>
              <ul className="space-y-3">
                <li className="flex items-center space-x-2">
                  <Phone size={16} />
                  <a href="tel:+61721503636" className="text-sm text-gray-300 hover:text-white transition-colors">07 2150 3636</a>
                </li>
                <li className="flex items-center space-x-2">
                  <Mail size={16} />
                  <a href="mailto:admin@navigatingminds.com.au" className="text-sm text-gray-300 hover:text-white transition-colors">admin@navigatingminds.com.au</a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-700 mt-8 pt-8 text-center">
            <p className="text-sm text-gray-400">
              &copy; {new Date().getFullYear()} Navigating Minds Psychology. All rights reserved. | Telehealth services available Australia-wide
            </p>
            <p className="text-sm text-gray-400 mt-2">
              Designed by <a href="https://www.minchsolutions.au" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">www.minchsolutions.au</a>
              {' '}|{' '}
              <Link to="/admin-login" className="hover:text-white transition-colors opacity-50">Admin</Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;